import { useEffect, useState } from "react";
import { CheckoutPhotosDialog } from "@/components/CheckoutPhotos";
import { DamageReportDialog } from "@/components/DamageReport";
import { CleanerGuideGate } from "@/components/CleanerGuideGate";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { CLEANING } from "@/constants/testIds";
import { Sparkles, Home, Calendar as CalIcon, User, KeyRound, Wifi, MessageSquare, Info, RefreshCw, Timer, MapPin, Camera, AlertTriangle } from "lucide-react";
import CleaningCostMatrix from "@/components/CleaningCostMatrix";
import BeddingMatrix from "@/components/BeddingMatrix";
import { SyncedChip } from "@/lib/synced";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const COLUMNS = [
  { key: "todo", label: "To do", tone: "border-slate-200" },
  { key: "in_progress", label: "In progress", tone: "border-amber-200 bg-amber-50/40" },
  { key: "done", label: "Done", tone: "border-teal-200 bg-teal-50/40" },
];

function CleanerWidget() {
  const [week, setWeek] = useState(null);
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsPreview, setSmsPreview] = useState(null); // { body, would_send, reason, turnovers, sent_to }
  const [smsOpen, setSmsOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/turnovers/my-week");
      setWeek(data);
      setLastSynced(new Date());
    } catch (err) {
      if (!silent) toast.error(apiError(err));
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  useEffect(() => {
    load(true);
    // Auto-refresh every 3 minutes so cleaners on the widget see fresh data
    const t = setInterval(() => load(true), 3 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const openSmsPreview = async () => {
    setSmsOpen(true);
    if (smsPreview) return;
    setSmsBusy(true);
    try {
      const { data } = await api.get("/turnovers/my-sms-preview");
      setSmsPreview(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSmsBusy(false);
    }
  };

  if (!week) return null;
  const today = week.today || { turnovers: [], date: "", count: 0 };
  const tomorrow = week.tomorrow || { turnovers: [], date: "", count: 0 };

  const TurnoverCard = ({ t }) => (
    <div
      key={t.booking_id}
      data-testid={`cleaner-widget-turnover-${t.booking_id}`}
      className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate flex items-center gap-1.5">
            <Home className="w-3.5 h-3.5 text-teal-700 shrink-0" />
            {t.property_name}
          </div>
          {t.property_address && (
            <div className="text-[11px] text-slate-500 mt-0.5 truncate">{t.property_address}</div>
          )}
          <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-1">
            <User className="w-3 h-3" /> {t.guest_name}
          </div>
        </div>
        <Sparkles className="w-4 h-4 text-teal-700/60 shrink-0" />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {t.door_code && (
          <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] bg-amber-50 text-amber-800 border border-amber-100">
            <KeyRound className="w-3 h-3" /> {t.door_code}
          </span>
        )}
        {t.wifi_password && (
          <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] bg-slate-50 text-slate-700 border border-slate-100">
            <Wifi className="w-3 h-3" /> {t.wifi_password}
          </span>
        )}
        {t.next_arrival && (
          <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] bg-teal-50 text-teal-700 border border-teal-100">
            next in {t.next_arrival}
          </span>
        )}
      </div>
      {t.checkin_instructions && (
        <div className="mt-3 text-[11px] text-slate-500 line-clamp-2 flex items-start gap-1.5">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{t.checkin_instructions}</span>
        </div>
      )}
    </div>
  );

  const Column = ({ label, day }) => (
    <div className="ns-card p-5" data-testid={`cleaner-widget-${label.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-serif text-lg inline-flex items-center gap-2">
          <CalIcon className="w-4 h-4 text-teal-700" />
          {label}
        </div>
        <span className="text-xs text-slate-400">
          {day.date} · {day.count} unit{day.count === 1 ? "" : "s"}
        </span>
      </div>
      <div className="space-y-3">
        {day.turnovers.map((t) => (
          <TurnoverCard key={t.booking_id} t={t} />
        ))}
        {day.turnovers.length === 0 && (
          <div className="text-[11px] text-slate-400 italic text-center py-8">
            No turnovers — {label.toLowerCase()} looks quiet.
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="mb-8" data-testid="cleaner-widget">
      <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="ns-label text-teal-700 mb-1 flex items-center gap-2 flex-wrap">
            <span>Your two-day view</span>
            <SyncedChip syncedAt={lastSynced} busy={refreshing} testid="cleaner-widget-last-synced" onRefresh={() => load(false)} />
          </div>
          <h2 className="font-serif text-2xl text-slate-900">
            Today & tomorrow at a glance
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="cleaner-widget-refresh"
            variant="outline"
            className="rounded-full h-10 px-4 gap-2"
            onClick={() => load(false)}
            disabled={refreshing}
          >
            <RefreshCw className={"w-4 h-4 " + (refreshing ? "animate-spin" : "")} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Button
            data-testid="cleaner-widget-preview-sms"
            variant="outline"
            className="rounded-full h-10 px-5 gap-2"
            onClick={openSmsPreview}
          >
            <MessageSquare className="w-4 h-4" />
            Preview my 07:00 SMS
          </Button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Column label="Today" day={today} />
        <Column label="Tomorrow" day={tomorrow} />
      </div>

      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent className="max-w-lg" data-testid="cleaner-sms-preview-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-teal-700" />
              Tomorrow's 07:00 UTC SMS
            </DialogTitle>
            <DialogDescription>
              This is exactly what will land on your phone at 07:00 UTC. It sends automatically as long as you have at least one turnover.
            </DialogDescription>
          </DialogHeader>
          {smsBusy && (
            <div className="text-center py-10 text-slate-400 italic">Composing your preview…</div>
          )}
          {!smsBusy && smsPreview && !smsPreview.would_send && (
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-600" data-testid="cleaner-sms-preview-empty">
              {smsPreview.reason || "No SMS scheduled — nothing on today's list."}
            </div>
          )}
          {!smsBusy && smsPreview?.would_send && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-teal-50 border border-teal-100 p-4">
                <pre className="text-sm text-slate-900 whitespace-pre-wrap font-serif leading-relaxed" data-testid="cleaner-sms-preview-body">
{smsPreview.body}
                </pre>
              </div>
              <div className="text-[11px] text-slate-500">
                {smsPreview.turnovers} turnover{smsPreview.turnovers === 1 ? "" : "s"} on the list · sending to {smsPreview.sent_to}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Cleaning() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const load = () =>
    api
      .get("/cleaning-tasks")
      .then((r) => setTasks(r.data))
      .catch((err) => toast.error(apiError(err)));

  useEffect(() => {
    load();
  }, []);

  const [photoTask, setPhotoTask] = useState(null);
  const [damageTask, setDamageTask] = useState(null);
  const move = async (task, status) => {
    if (status === "done" && isCleaner && !task._photosReady) { setPhotoTask(task); return; }
    try {
      await api.patch(`/cleaning-tasks/${task.id}`, { status });
      toast.success(`Moved to ${status.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const grouped = COLUMNS.reduce((acc, c) => {
    acc[c.key] = tasks.filter((t) => t.status === c.key);
    return acc;
  }, {});

  const isCleaner = user?.role === "cleaner";

  return (
    <Layout
      title={isCleaner ? "Your turnovers" : "Cleaning tasks"}
      subtitle={
        isCleaner
          ? "Everything pinned to you across the next 14 days. Door codes + Wi-Fi are visible on today and tomorrow's cards."
          : "Auto-generated from every checkout in the next 14 days. Drag with the buttons below each card."
      }
    >
      {isCleaner && <div className="mb-4"><CleanerGuideGate /></div>}
      {isCleaner && <CleanerWidget />}
      {!isCleaner && (
        <div className="mb-6">
          <CleaningCostMatrix />
          <BeddingMatrix />
        </div>
      )}
      <div data-testid={CLEANING.root} className="grid md:grid-cols-3 gap-4">
        {COLUMNS.map((col) => (
          <div
            key={col.key}
            data-testid={CLEANING.column(col.key)}
            className={`ns-card p-5 ${col.tone} min-h-[400px]`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-serif text-lg">{col.label}</div>
              <span className="text-xs text-slate-400">
                {grouped[col.key].length}
              </span>
            </div>
            <div className="space-y-3">
              {grouped[col.key].map((t) => (
                <div
                  key={t.id}
                  data-testid={CLEANING.card(t.id)}
                  className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        <Home className="w-3.5 h-3.5 inline mr-1 text-teal-700" />
                        {t.property_name}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                        <CalIcon className="w-3 h-3" /> {t.date}
                      </div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                        <User className="w-3 h-3" /> {t.guest_name}
                      </div>
                    </div>
                    <Sparkles className="w-4 h-4 text-teal-700/60 shrink-0" />
                  </div>
                  {t.cleaner_name && (
                    <div className="mt-2 text-[11px] text-teal-700">
                      Assigned to {t.cleaner_name}
                    </div>
                  )}
                  {t.cleaning_company_name && (
                    <div
                      data-testid={`cleaning-task-company-${t.id}`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5"
                    >
                      {t.cleaning_company_name}
                    </div>
                  )}
                  {/* Session Z88 — turnover timer + GPS proof */}
                  {t.minutes_on_site !== undefined && t.minutes_on_site !== null && (
                    <div
                      data-testid={`cleaning-task-timer-${t.id}`}
                      className="mt-2 inline-flex items-center gap-1 text-[10px] text-teal-800 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5"
                      title={t.started_at ? `Started ${new Date(t.started_at).toLocaleTimeString()} · completed ${new Date(t.completed_at).toLocaleTimeString()}` : ""}
                    >
                      <Timer className="w-3 h-3" /> {t.minutes_on_site} min on site
                    </div>
                  )}
                  {t.status === "in_progress" && t.started_at && (
                    <div
                      data-testid={`cleaning-task-live-${t.id}`}
                      className="mt-2 inline-flex items-center gap-1 text-[10px] text-amber-800 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5"
                      title={`Started ${new Date(t.started_at).toLocaleTimeString()}`}
                    >
                      <Timer className="w-3 h-3" /> {Math.max(0, Math.round((Date.now() - new Date(t.started_at).getTime()) / 60000))} min live
                    </div>
                  )}
                  {t.started_geo && (
                    <div
                      data-testid={`cleaning-task-geo-${t.id}`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-600 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5"
                      title={`GPS fix at arrival · accuracy ±${Math.round(t.started_geo.accuracy || 0)} m`}
                    >
                      <MapPin className="w-3 h-3" /> GPS ±{Math.round(t.started_geo.accuracy || 0)}m
                    </div>
                  )}
                  {/* Session Z35 — star rating widget (once cleaner marks done) */}
                  {t.status === "done" && (
                    <div className="mt-2 flex items-center gap-0.5" data-testid={`cleaning-task-rating-${t.id}`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`cleaning-task-star-${t.id}-${n}`}
                          onClick={async () => {
                            try {
                              await api.patch(`/cleaning-tasks/${t.id}`, { rating: n });
                              load();
                            } catch (err) {
                              toast.error("Rating failed");
                            }
                          }}
                          className={`text-lg leading-none transition ${
                            (t.rating || 0) >= n ? "text-amber-500" : "text-slate-200 hover:text-amber-300"
                          }`}
                          title={`Rate ${n} star${n === 1 ? "" : "s"}`}
                        >
                          ★
                        </button>
                      ))}
                      {t.rating && (
                        <span className="ml-1 text-[10px] text-slate-400">Rated</span>
                      )}
                    </div>
                  )}
                  {t.source && t.source !== "internal" && (
                    <div className="mt-1 text-[10px] tracking-widest uppercase text-slate-400">
                      via {t.source}
                    </div>
                  )}
                  <div className="mt-3 flex gap-1.5 flex-wrap">
                    <button onClick={() => setDamageTask(t)} data-testid={`cleaning-report-damage-${t.id}`}
                      className="text-[11px] tracking-wider uppercase px-2.5 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Report damage
                    </button>
                    <button onClick={() => setPhotoTask(t)} data-testid={`cleaning-photos-${t.id}`}
                      className="text-[11px] tracking-wider uppercase px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-teal-600 hover:text-teal-700 inline-flex items-center gap-1">
                      <Camera className="w-3 h-3" /> Photos{t.photo_count ? ` · ${t.photo_count}` : ""}
                    </button>
                    {COLUMNS.filter((c) => c.key !== col.key).map((c) => (
                      <button
                        key={c.key}
                        data-testid={CLEANING.moveTo(t.id, c.key)}
                        onClick={() => move(t, c.key)}
                        className="text-[11px] tracking-wider uppercase px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-teal-600 hover:text-teal-700"
                      >
                        → {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {grouped[col.key].length === 0 && (
                <div className="text-[11px] text-slate-400 italic text-center py-8">
                  Nothing here.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {damageTask && <DamageReportDialog task={damageTask} onClose={() => setDamageTask(null)} />}
      {photoTask && (
        <CheckoutPhotosDialog task={photoTask} user={user} onClose={() => { setPhotoTask(null); load(); }}
          onReady={() => { const t = { ...photoTask, _photosReady: true }; setPhotoTask(null); move(t, "done"); }} />
      )}
    </Layout>
  );
}
